import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download, Eye, Search, RotateCcw, Filter, Loader2, ClipboardList,
  FolderDown, CheckSquare, Square,
} from "lucide-react";
import { useCustomerAuth, customerFetch } from "./CustomerAuthContext";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; const [y,m,d]=String(iso).split("-"); return y&&m&&d?`${d}-${m}-${y}`:iso; };

const DOC_TYPES = ["All", "Invoice", "Credit Note", "Debit Note", "Delivery Challan", "E-Way Bill", "Receipt"];
const STATUSES = ["All", "Open", "Paid", "Partially Paid", "Cancelled", "Overdue"];
const STATUS_CLR = {
  Open: "bg-sky-100 text-sky-700",
  Paid: "bg-emerald-100 text-emerald-700",
  "Partially Paid": "bg-amber-100 text-amber-700",
  Cancelled: "bg-slate-100 text-slate-500",
  Overdue: "bg-red-100 text-red-700",
};
const PAGE_SIZE = 25;
const EMPTY = { q: "", doc_type: "All", status: "All", po_number: "", date_from: "", date_to: "" };

export default function CustomerSalesDocuments() {
  const { token } = useCustomerAuth();
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ by: "doc_date", dir: "desc" });
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [poOptions, setPoOptions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchDocs = useCallback(async (filters, pageNum, srt) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        q: filters.q, doc_type: filters.doc_type, status: filters.status, po_number: filters.po_number,
        date_from: filters.date_from, date_to: filters.date_to,
        page: String(pageNum), page_size: String(PAGE_SIZE),
        sort_by: srt.by, sort_dir: srt.dir,
      });
      const r = await customerFetch(token)(`/api/customer/sales-documents?${qs.toString()}`);
      setResponse(await r.json());
    } catch { setResponse({ items: [], total: 0, total_pages: 1, page: 1 }); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    customerFetch(token)("/api/customer/sales-documents/filters")
      .then((r) => r.json()).then((d) => setPoOptions(d.po_numbers || [])).catch(() => setPoOptions([]));
  }, [token]);

  useEffect(() => { fetchDocs(applied, page, sort); }, [applied, page, sort, fetchDocs]);

  const applyFilters = () => { setApplied(draft); setPage(1); setSelected(new Set()); };
  const clearFilters = () => { setDraft(EMPTY); setApplied(EMPTY); setPage(1); setSelected(new Set()); };

  const total = response?.total ?? 0;
  const totalPages = response?.total_pages ?? 1;
  const items = response?.items ?? [];
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (applied.q) n++;
    if (applied.doc_type && applied.doc_type !== "All") n++;
    if (applied.status && applied.status !== "All") n++;
    if (applied.po_number) n++;
    if (applied.date_from) n++;
    if (applied.date_to) n++;
    return n;
  }, [applied]);

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((d) => d.id)));
  };
  const toggleOne = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };

  const downloadDoc = async (d) => {
    const r = await fetch(`${API_BASE}/api/customer/sales-documents/${d.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return alert("Download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = d.filename || `${d.doc_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const previewDoc = (d) => {
    const url = `${API_BASE}/api/customer/sales-documents/${d.id}/preview?token=${encodeURIComponent(token)}`;
    // Since we can't set Authorization on window.open, use blob approach:
    fetch(`${API_BASE}/api/customer/sales-documents/${d.id}/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : Promise.reject())
      .then((blob) => {
        const bu = URL.createObjectURL(blob);
        window.open(bu, "_blank");
        setTimeout(() => URL.revokeObjectURL(bu), 60000);
      })
      .catch(() => alert("Preview failed"));
  };

  const bulkDownload = async (mode) => {
    // mode: 'selected' | 'date-range'
    setBulkBusy(true);
    try {
      let body = {};
      if (mode === "selected") {
        if (selected.size === 0) { setBulkBusy(false); return alert("Select at least one document."); }
        body = { ids: Array.from(selected) };
      } else {
        if (!applied.date_from && !applied.date_to && applied.doc_type === "All" && applied.status === "All") {
          setBulkBusy(false); return alert("Please set at least a date range or filter to bulk-download.");
        }
        body = {
          date_from: applied.date_from, date_to: applied.date_to,
          doc_type: applied.doc_type, status: applied.status,
        };
      }
      const r = await fetch(`${API_BASE}/api/customer/sales-documents/bulk-download`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Bulk download failed");
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `baishalii-documents-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    finally { setBulkBusy(false); }
  };

  const setSortBy = (col) => {
    if (sort.by === col) setSort({ by: col, dir: sort.dir === "asc" ? "desc" : "asc" });
    else setSort({ by: col, dir: "desc" });
  };
  const sortIndicator = (col) => sort.by === col ? (sort.dir === "asc" ? " ↑" : " ↓") : "";

  return (
    <div className="space-y-4" data-testid="customer-sales-docs">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Sales Documents</h1>
          <p className="text-sm text-slate-500 mt-1">Invoices, credit / debit notes, delivery challans, e-way bills and receipts.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => bulkDownload("selected")} disabled={bulkBusy || selected.size === 0}
            data-testid="bulk-download-selected"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FolderDown className="h-4 w-4" /> Download selected {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
          <button
            onClick={() => bulkDownload("date-range")} disabled={bulkBusy}
            data-testid="bulk-download-range"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0a2a5e] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0a2a5e]/90 disabled:opacity-40"
          >
            <FolderDown className="h-4 w-4" /> Download by filter
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Search by Document No, PO No…" value={draft.q}
              onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              data-testid="doc-search-input"
              className="w-full rounded-lg border border-slate-200 pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a2a5e]/20 focus:border-[#0a2a5e]" />
          </div>
          {activeFilterCount > 0 && (
            <span className="hidden md:inline-flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
              <Filter className="h-3 w-3" /> {activeFilterCount} active
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">From</label>
            <input type="date" value={draft.date_from} onChange={(e) => setDraft({ ...draft, date_from: e.target.value })} data-testid="doc-date-from"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">To</label>
            <input type="date" value={draft.date_to} onChange={(e) => setDraft({ ...draft, date_to: e.target.value })} data-testid="doc-date-to"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Document Type</label>
            <select value={draft.doc_type} onChange={(e) => setDraft({ ...draft, doc_type: e.target.value })} data-testid="doc-type-filter"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
            <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="doc-status-filter"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
              {STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">PO Number</label>
            <input list="cust-po-list" value={draft.po_number} onChange={(e) => setDraft({ ...draft, po_number: e.target.value })}
              placeholder="All POs" data-testid="doc-po-filter"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <datalist id="cust-po-list">{poOptions.map((p) => <option key={p} value={p} />)}</datalist>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={clearFilters} data-testid="doc-clear-filters"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <RotateCcw className="h-4 w-4" /> Clear Filters
          </button>
          <button onClick={applyFilters} data-testid="doc-fetch"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0a2a5e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2a5e]/90">
            <Search className="h-4 w-4" /> Fetch
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-3 py-3 w-10">
                <button onClick={toggleAll} data-testid="select-all" className="text-slate-500 hover:text-[#0a2a5e]">
                  {selected.size === items.length && items.length > 0 ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                </button>
              </th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold cursor-pointer" onClick={() => setSortBy("doc_number")}>Document No{sortIndicator("doc_number")}</th>
              <th className="px-4 py-3 font-semibold cursor-pointer" onClick={() => setSortBy("doc_date")}>Date{sortIndicator("doc_date")}</th>
              <th className="px-4 py-3 font-semibold">PO Number</th>
              <th className="px-4 py-3 font-semibold text-right cursor-pointer" onClick={() => setSortBy("amount")}>Amount{sortIndicator("amount")}</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <ClipboardList className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="font-semibold text-slate-700 mb-1">{activeFilterCount > 0 ? "No documents match your filters" : "No documents yet"}</p>
                  <p className="text-sm text-slate-500">
                    {activeFilterCount > 0
                      ? <>Try adjusting or <button onClick={clearFilters} className="text-[#0a2a5e] hover:underline font-semibold">clear all filters</button>.</>
                      : "Your uploaded invoices, delivery notes, credit notes and receipts will appear here."}
                  </p>
                </td>
              </tr>
            )}
            {!loading && items.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-3 py-3">
                  <button onClick={() => toggleOne(d.id)} data-testid={`select-doc-${d.id}`} className="text-slate-500 hover:text-[#0a2a5e]">
                    {selected.has(d.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">{d.doc_type}</span>
                </td>
                <td className="px-4 py-3 font-semibold text-[#0a2a5e]">{d.doc_number}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(d.doc_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.po_number || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">₹ {inr(d.amount)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_CLR[d.status] || "bg-slate-100"}`}>{d.status}</span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => previewDoc(d)} data-testid={`doc-preview-${d.id}`} className="text-slate-500 hover:text-[#0a2a5e] p-1.5" title="Preview PDF"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => downloadDoc(d)} data-testid={`doc-download-${d.id}`} className="text-emerald-600 hover:text-emerald-800 p-1.5" title="Download PDF"><Download className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {response && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-1" data-testid="doc-pagination">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">Page {response.page} of {totalPages}</span>
            <span className="mx-2 text-slate-300">|</span>
            <span>Total Entries: <span className="font-semibold text-slate-800">{total}</span></span>
            {total > 0 && <>
              <span className="mx-2 text-slate-300">|</span>
              <span className="text-slate-500">Showing {rangeStart}–{rangeEnd}</span>
            </>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1 || loading} className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">« First</button>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading} className="px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">‹ Prev</button>
            <span className="px-3 py-1.5 text-sm font-semibold text-slate-700">{response.page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} className="px-3 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">Next ›</button>
            <button onClick={() => setPage(totalPages)} disabled={page >= totalPages || loading} className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">Last »</button>
          </div>
        </div>
      )}
    </div>
  );
}
